import React from 'react'
import { Close } from '@mui/icons-material'
import {
  Button,
  createTheme,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogProps,
  Grid,
  IconButton,
  SxProps,
  Theme,
  ThemeProvider,
  Typography,
  useTheme,
} from '@mui/material'

import { generateTheme } from '../../providers/mui-theme-provider'
import BlurSkrim from '../blur-skrim'

import classes from './basic-dialog.styles'

export interface IBasicDialogProps extends DialogProps {
  title?: string
  children?: React.ReactNode
  textContent?: string | React.ReactNode | React.ReactNode[] | React.JSX.Element | (() => React.JSX.Element)
  onClose: () => void
  closeLabel?: string
  hideCancelButton?: boolean
  onConfirm?: () => void
  confirmLabel?: string
  confirmButton?: React.ReactNode
  confirmLoading?: boolean
  icon?: React.ReactNode
  showCloseIcon?: boolean
  rightIcon?: React.ReactNode
  contentContainerSx?: SxProps<Theme>
  paperSx?: SxProps<Theme>
  disableBackdropClick?: boolean
  variant?: 'default' | 'hsp-plus'
}

const BasicDialog = (props: IBasicDialogProps) => {
  const {
    onClose,
    closeLabel,
    hideCancelButton = false,
    onConfirm,
    confirmLabel,
    confirmLoading,
    confirmButton,
    title,
    icon,
    children,
    textContent,
    showCloseIcon,
    rightIcon,
    contentContainerSx,
    paperSx,
    disableBackdropClick = false,
    variant = 'default',
    maxWidth = 'xs',
    ...rest
  } = props

  const theme = useTheme()
  const isHspPlus = variant === 'hsp-plus'

  return (
    // Force light theme for dialog to ensure it looks correct when used in dark mode screens
    <ThemeProvider theme={createTheme(generateTheme('light'))}>
      <CssBaseline />
      <Dialog
        onClose={onClose}
        slotProps={{
          paper: {
            sx: [classes.dialogPaper(theme, isHspPlus), ...(Array.isArray(paperSx) ? paperSx : [paperSx])],
          },
        }}
        slots={{
          backdrop: (backdropProps) => (
            <BlurSkrim
              {...backdropProps}
              onClick={() => {
                if (!disableBackdropClick) {
                  props.onClose?.()
                }
              }}
            />
          ),
        }}
        maxWidth={maxWidth}
        {...rest}
      >
        {showCloseIcon || title ? (
          <Grid container={true} size={12} sx={classes.headerContainer(theme, !!showCloseIcon, isHspPlus)}>
            {showCloseIcon && (
              <Grid>
                <IconButton onClick={onClose} data-testid="close-modal-btn" aria-label="Close dialog" sx={classes.closeButton}>
                  <Close />
                </IconButton>
              </Grid>
            )}
            {title && (
              <Grid sx={classes.titleContainer}>
                <Typography variant={isHspPlus ? 'subtitle2' : 'h6'} color="textPrimary" sx={classes.titleText(!!showCloseIcon && !rightIcon)}>
                  {title}
                </Typography>
              </Grid>
            )}
            {rightIcon && <Grid sx={classes.rightIcon}>{rightIcon}</Grid>}
          </Grid>
        ) : null}

        <Grid
          container={true}
          sx={[classes.contentContainer(theme, isHspPlus), ...(Array.isArray(contentContainerSx) ? contentContainerSx : [contentContainerSx])]}
        >
          {icon}
          {typeof textContent === 'string' ? (
            <Typography variant="body1" color="textPrimary">
              {textContent}
            </Typography>
          ) : typeof textContent === 'function' ? (
            textContent()
          ) : (
            <Grid size={12}>{textContent}</Grid>
          )}
          {children && !textContent && children}
        </Grid>

        {!hideCancelButton || confirmLabel || confirmButton ? (
          <DialogActions sx={{ ...classes.dialogActions(theme, !hideCancelButton && (!!confirmLabel || !!confirmButton), isHspPlus) }}>
            {!hideCancelButton && <Button onClick={onClose}>{closeLabel || 'Cancel'}</Button>}
            {confirmLabel && !confirmButton && (
              <Button variant="contained" disableElevation onClick={onConfirm} disabled={confirmLoading} loading={confirmLoading}>
                {confirmLabel}
              </Button>
            )}
            {!confirmLabel && confirmButton && confirmButton}
          </DialogActions>
        ) : null}
      </Dialog>
    </ThemeProvider>
  )
}

export default BasicDialog
